import {createContext, useContext} from 'react'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

interface BankAccountsService {
  create: (payload: any) => Promise<any>
  list: (query: any) => Promise<any>
  get: (id: string) => Promise<any>
  update: (id: string, payload: any) => Promise<any>
  requestDelete: (id: string, payload: any) => Promise<any>
  delete: (id: string) => Promise<any>
}

const BankAccountsServiceContext = createContext<BankAccountsService | undefined>(undefined)

interface BankAccountsServiceProviderProps {
  children: any
  baseApiHost: string
  handleUnauthorized?: () => void
}

const BankAccountsServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}: BankAccountsServiceProviderProps) => {
  return (
    <ResourceApiServiceProvider
      Context={BankAccountsServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      valueCallback={(axios: any) => ({
        create: async (payload: any) => {
          const {data} = await axios.post(`/api/v1/org/bank_accounts`, payload)
          return data
        },
        list: async (query: any) => {
          const {data} = await axios.get(
            `/api/v1/org/bank_accounts${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        get: async (id: string) => {
          const {data} = await axios.get(`/api/v1/org/bank_accounts/${id}`)
          return data
        },
        update: async (id: string, payload: any) => {
          const {data} = await axios.patch(`/api/v1/org/bank_accounts/${id}`, payload)
          return data
        },
        requestDelete: async (id: string, payload: any) => {
          const {data} = await axios.patch(`/api/v1/org/bank_accounts/${id}/request_delete`, payload)
          return data
        },
        delete: async (id: string) => {
          const {data} = await axios.delete(`/api/v1/org/bank_accounts/${id}`)
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

const useBankAccountsService = () => {
  const context = useContext(BankAccountsServiceContext)
  if (context === undefined) {
    throw new Error('BankAccountsServiceContext was not provided. Make sure your component is a child of the BankAccountsServiceProvider.')
  }
  return context
}

interface UseBankAccountsQueryProps {
  query: any
  [key: string]: any
}

const useBankAccountsQuery = ({query, ...otherProps}: UseBankAccountsQueryProps) => {
  const bankAccountsService = useBankAccountsService()
  return useSimpleQuery({
    queryKey: `bank_accounts-list-${JSON.stringify(query)}`,
    queryFn: async () => bankAccountsService.list(query),
    ...otherProps
  })
}

interface UseBankAccountQueryProps {
  id: string
  enabled?: boolean
  [key: string]: any
}

const useBankAccountQuery = ({id, enabled, ...otherProps}: UseBankAccountQueryProps) => {
  const bankAccountsService = useBankAccountsService()
  return useSimpleQuery({
    queryKey: `bank_accounts-get-${id}`,
    queryFn: async () => bankAccountsService.get(id),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

export {BankAccountsServiceProvider, useBankAccountsService, useBankAccountsQuery, useBankAccountQuery}
